import { useEffect, useMemo } from 'react';
import { useSocket } from './SocketProvider';
import { LiveFeed, LiveFeedEvent, LiveFeedEventType, PaginatedResponse } from '@repo/types';
import { Match, SocketEventType, SocketMessage } from '@repo/types';
import { useLiveFeedQuery } from '@data/liveFeed/useLiveFeedQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useTodaysMatchesQuery } from '@data/matches/useTodaysMatchesQuery';

export type EnahncedLiveFeedEvent = Omit<LiveFeedEvent, 'match'> & {
  match: Match | null;
};
export type EnhancedLiveFeed = {
  date: string;
  events: EnahncedLiveFeedEvent[];
};

export const useLiveFeed = (): EnhancedLiveFeed | null => {
  const socket = useSocket();
  const queryClient = useQueryClient();

  const matches = useTodaysMatchesQuery({ withLiveScores: true });

  const liveFeedDate = useMemo(() => {
    // return '2025-08-25';
    const today = new Date();
    return today.toISOString().substring(0, 10);
  }, []);

  const liveFeed = useLiveFeedQuery(liveFeedDate);

  useEffect(() => {
    socket.on('message', (msg: SocketMessage) => {
      if (msg.type === SocketEventType.NewLiveFeedEvents) {
        const today = new Date();
        const todaysDate = today.toISOString().substring(0, 10);

        const newLiveFeed = msg.payload;
        queryClient.setQueryData(['live-feed', { date: newLiveFeed.date }], (old: LiveFeed) => {
          if (!old) return newLiveFeed;
          return {
            ...old,
            events: [...newLiveFeed.events, ...old.events],
          };
        });

        // Update the matches list cache
        queryClient.setQueryData(
          [
            'matches',
            {
              period: 'Custom',
              startDate: `${todaysDate}T00:00:00`,
              endDate: `${todaysDate}T23:59:59`,
              limit: 300,
              withLiveScores: true,
            },
          ],
          (cachedMatches: PaginatedResponse<Match>) => {
            // If matches not found in cache - don't do anything
            if (!cachedMatches) return null;

            // If matches found in cache - find each match related to the live events and update the match details in cache
            return {
              ...cachedMatches,
              items: cachedMatches.items.map((match) => {
                const event = newLiveFeed.events.find((event) => event.match === match.id);
                if (!event) return match;

                const isHomeTeam = match.homeTeam.id === event.person.teamId;

                switch (event.eventType) {
                  case LiveFeedEventType.Goal:
                  case LiveFeedEventType.GoalFromPenalty:
                    return {
                      ...match,
                      score: {
                        current: {
                          home: event.score.home,
                          away: event.score.away,
                        },
                      },
                    };
                  case LiveFeedEventType.OwnGoal:
                    return {
                      ...match,
                      score: isHomeTeam
                        ? {
                            ...match.score,
                            away: event.score.away,
                          }
                        : {
                            ...match.score,
                            home: event.score.home,
                          },
                    };
                  default:
                    return match;
                }
              }),
            };
          }
        );

        // Update the match details cache
        newLiveFeed.events.forEach((event) => {
          switch (event.eventType) {
            case LiveFeedEventType.Goal:
              queryClient.setQueryData(['match', { id: event.match }], (cachedMatch: Match | null) => {
                if (!cachedMatch) return null;
                const isHomeTeam = cachedMatch.homeTeam.id === event.person.teamId;

                switch (event.eventType) {
                  case LiveFeedEventType.Goal:
                  case LiveFeedEventType.GoalFromPenalty:
                    return {
                      ...cachedMatch,
                      score: {
                        current: {
                          home: event.score.home,
                          away: event.score.away,
                        },
                      },
                      lineups: {
                        home: !isHomeTeam
                          ? cachedMatch.lineups?.home
                          : cachedMatch.lineups?.home?.map((lineup) => {
                              // this is the player who just scored a goal - add the goal to his record
                              if (lineup.player.id === event.person.id) {
                                return {
                                  ...lineup,
                                  goals: [
                                    ...(lineup.goals ?? []),
                                    {
                                      player: lineup.player,
                                      minute: event.minute,
                                      sort: lineup.goals ? lineup.goals.length + 1 : 1,
                                    },
                                  ],
                                };
                              }
                              return lineup;
                            }),
                        away: isHomeTeam
                          ? cachedMatch.lineups?.away
                          : cachedMatch.lineups?.away?.map((lineup) => {
                              // this is the player who just scored a goal - add the goal to his record
                              if (lineup.player.id === event.person.id) {
                                return {
                                  ...lineup,
                                  goals: [
                                    ...(lineup.goals ?? []),
                                    {
                                      player: lineup.player,
                                      minute: event.minute,
                                      sort: lineup.goals ? lineup.goals.length + 1 : 1,
                                    },
                                  ],
                                };
                              }
                              return lineup;
                            }),
                      },
                    };
                  case LiveFeedEventType.OwnGoal:
                    return {
                      ...cachedMatch,
                      score: isHomeTeam
                        ? {
                            ...cachedMatch.score,
                            away: event.score.away,
                          }
                        : {
                            ...cachedMatch.score,
                            home: event.score.home,
                          },
                    };
                  default:
                    return cachedMatch;
                }
              });
              break;
            case LiveFeedEventType.OwnGoal:
              queryClient.setQueryData(['match', { id: event.match }], (old: Match | null) => {
                if (!old) return null;
                return {
                  ...old,
                  score: event.score,
                };
              });
              break;
          }
        });
      }
    });

    // cleanup listener when leaving this page
    return () => {
      socket.off('message');
    };
  }, [socket, queryClient]);

  const enhancedLiveFeed = useMemo(() => {
    if (liveFeed?.data?.events && matches?.data?.items) {
      const enhancedEvents = liveFeed.data.events.map((event) => {
        const match = matches.data.items.find((match) => match.id === event.match);

        return {
          ...event,
          match: match ?? null,
        };
      });

      return {
        date: liveFeed.data._id,
        events: enhancedEvents,
      };
    }

    return null;
  }, [liveFeed?.data?._id, liveFeed?.data?.events, matches?.data?.items]);

  return enhancedLiveFeed;
};
